import dayjs, { type Dayjs } from 'dayjs';

import { StreakDay, StreakSummary } from 'src/types/streak.types';
import { toDayKey } from 'src/utils/date';

export type StreakDayState =
  | 'completed'
  | 'milestone'
  | 'freeze'
  | 'lost'
  | 'upcoming';

type LiveRun = Pick<StreakSummary, 'started_on' | 'last_day'>;

const MAX_RUN_DAYS = 1000;

export const countedDatesOf = (
  days: StreakDay[] | undefined,
  liveRun?: LiveRun | null,
): Set<string> => {
  const dates = new Set((days ?? []).map(day => day.date));
  if (!liveRun?.started_on || !liveRun.last_day) return dates;

  let cursor = dayjs(liveRun.last_day);
  const start = dayjs(liveRun.started_on);
  for (let guard = 0; !cursor.isBefore(start, 'day') && guard < MAX_RUN_DAYS; guard += 1) {
    dates.add(toDayKey(cursor));
    cursor = cursor.subtract(1, 'day');
  }
  return dates;
};

export const isStreakActivatedOn = (
  summary: Pick<StreakSummary, 'last_day'> | undefined,
  today: Dayjs = dayjs(),
): boolean =>
  summary?.last_day != null && !dayjs(summary.last_day).isBefore(today, 'day');

const has = (dates: Set<string>, day: Dayjs) =>
  dates.has(toDayKey(day));

const runLengthEndingOn = (dates: Set<string>, day: Dayjs): number => {
  let length = 0;
  let cursor = day;
  while (has(dates, cursor)) {
    length += 1;
    cursor = cursor.subtract(1, 'day');
  }
  return length;
};

const runLengthContaining = (dates: Set<string>, day: Dayjs): number => {
  if (!has(dates, day)) return 0;
  let length = 1;
  let back = day.subtract(1, 'day');
  while (has(dates, back)) {
    length += 1;
    back = back.subtract(1, 'day');
  }
  let forward = day.add(1, 'day');
  while (has(dates, forward)) {
    length += 1;
    forward = forward.add(1, 'day');
  }
  return length;
};

export const isRunWelded = (
  day: Dayjs,
  dates: Set<string>,
  celebrationTiers: number[] = [],
  goal?: { targetDays: number; baselineStreak: number } | null,
): boolean => {
  const length = runLengthContaining(dates, day);
  if (length === 0) return false;
  if (celebrationTiers.includes(length)) return true;
  return goal ? length - goal.baselineStreak >= goal.targetDays : false;
};

export const streakDayState = (
  day: Dayjs,
  dates: Set<string>,
  today: Dayjs = dayjs(),
  freezeDates: Set<string> = new Set(),
  celebrationTiers: number[] = [],
): StreakDayState => {
  if (has(freezeDates, day)) return 'freeze';
  if (has(dates, day)) {
    return celebrationTiers.includes(runLengthEndingOn(dates, day))
      ? 'milestone'
      : 'completed';
  }
  if (!day.isBefore(today, 'day')) return 'upcoming';
  return 'lost';
};

export const streakRunEdges = (
  day: Dayjs,
  dates: Set<string>,
  displayedMonth?: Dayjs,
): { isRunStart: boolean; isRunEnd: boolean } => {
  const isOutOfMonth = (other: Dayjs) =>
    displayedMonth !== undefined && !other.isSame(displayedMonth, 'month');
  return {
    isRunStart:
      isOutOfMonth(day.subtract(1, 'day')) || !has(dates, day.subtract(1, 'day')),
    isRunEnd: isOutOfMonth(day.add(1, 'day')) || !has(dates, day.add(1, 'day')),
  };
};
